<template>
  <transition name="vip-pay-fade">
    <div
      v-if="visible"
      class="vip-pay-root fixed inset-0 z-[10060] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label="会员支付"
    >
      <div class="absolute inset-0 bg-black/65 backdrop-blur-sm" @click="onClose"></div>
      <div
        class="vip-pay-panel relative z-10 w-full max-w-[420px] overflow-hidden rounded-2xl border border-white/[0.08] bg-[#1e1e22] text-white shadow-2xl"
        @click.stop
      >
        <button
          type="button"
          class="absolute right-3 top-3 z-20 flex h-8 w-8 items-center justify-center rounded-full text-white/55 transition hover:bg-white/10 hover:text-white"
          aria-label="关闭"
          @click="onClose"
        >
          <i class="fas fa-times text-sm"></i>
        </button>

        <div class="px-6 pb-4 pt-8 text-center">
          <h2 class="text-[15px] font-semibold leading-snug tracking-tight text-white/95">
            {{ title }}
          </h2>

          <div class="mt-5 flex items-center justify-center gap-2 text-[12px] text-white/55">
            <i class="fab fa-alipay text-[20px] text-[#1677ff]" aria-hidden="true"></i>
            <span>请在</span>
            <span class="font-mono tabular-nums tracking-wider text-white/90">{{ countdownText }}</span>
            <span>内扫码完成支付</span>
          </div>

          <div class="mt-5 flex flex-wrap items-end justify-center gap-x-2 gap-y-1">
            <span class="text-[32px] font-bold leading-none tabular-nums text-[#3b9eff]">¥{{ priceMain }}</span>
            <span
              v-if="priceOld"
              class="pb-1 text-[15px] font-medium tabular-nums text-white/35 line-through decoration-white/25"
              >¥{{ priceOld }}</span
            >
            <span
              v-if="discountLabel"
              class="mb-1 rounded-md bg-[#e85d4a] px-2 py-0.5 text-[11px] font-semibold text-white"
              >{{ discountLabel }}</span
            >
          </div>

          <div class="mx-auto mt-6 w-[220px] rounded-xl border-[3px] border-white bg-white p-2.5 shadow-inner">
            <img
              v-if="qrImageSrc && !qrLoading"
              :src="qrImageSrc"
              alt="支付二维码"
              class="mx-auto block h-[196px] w-[196px] object-contain"
              @error="onQrError"
            />
            <div
              v-else
              class="flex h-[196px] w-[196px] items-center justify-center bg-white text-center text-[11px] text-gray-500"
            >
              {{ qrError ? '二维码生成失败' : '正在生成二维码…' }}
            </div>
          </div>

          <p v-if="renewNote" class="mt-4 text-[11px] leading-relaxed text-white/40">{{ renewNote }}</p>
        </div>

        <div class="border-t border-white/[0.06] bg-[#18181c] px-4 py-3 text-center text-[11px] text-white/45">
          开通即代表同意
          <a
            class="text-white/65 no-underline hover:text-sky-300 hover:underline"
            href="/vip-agreement"
            target="_blank"
            rel="noopener noreferrer"
            >《idoing 会员服务协议》</a
          >
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { generatePayQrDataUrl } from '../utils/payQrCode'

export default {
  name: 'VipPayModal',
  props: {
    visible: { type: Boolean, default: false },
    title: { type: String, default: '' },
    priceMain: { type: String, default: '0' },
    priceOld: { type: String, default: '' },
    discountLabel: { type: String, default: '' },
    qrCode: { type: String, default: '' },
    renewNote: { type: String, default: '' },
    /** 支付剩余秒数 */
    expireSeconds: { type: Number, default: 900 }
  },
  data() {
    return {
      remainSec: 0,
      timerId: null,
      qrError: false,
      qrImageSrc: '',
      qrLoading: false
    }
  },
  computed: {
    countdownText() {
      const s = Math.max(0, this.remainSec)
      const h = Math.floor(s / 3600)
      const m = Math.floor((s % 3600) / 60)
      const sec = s % 60
      const pad = n => String(n).padStart(2, '0')
      if (h > 0) return `${pad(h)} : ${pad(m)} : ${pad(sec)}`
      return `${pad(m)} : ${pad(sec)}`
    }
  },
  watch: {
    visible(v) {
      if (v) this.onOpen()
      else this.onShut()
    },
    qrCode: {
      immediate: true,
      handler() {
        this.refreshQrSrc()
      }
    }
  },
  beforeDestroy() {
    this.clearTimer()
  },
  methods: {
    async refreshQrSrc() {
      const raw = this.qrCode == null ? '' : String(this.qrCode).trim()
      if (!raw) {
        this.qrImageSrc = ''
        this.qrError = true
        this.qrLoading = false
        return
      }
      this.qrError = false
      this.qrLoading = true
      this.qrImageSrc = ''
      try {
        this.qrImageSrc = await generatePayQrDataUrl(raw, { width: 240 })
        if (!this.qrImageSrc) this.qrError = true
      } catch (e) {
        this.qrError = true
        this.qrImageSrc = ''
      } finally {
        this.qrLoading = false
      }
    },
    onQrError() {
      this.qrError = true
    },
    onOpen() {
      this.remainSec = Math.max(60, Number(this.expireSeconds) || 900)
      this.refreshQrSrc()
      this.clearTimer()
      this.timerId = window.setInterval(() => {
        if (this.remainSec <= 0) {
          this.clearTimer()
          this.$emit('expired')
          return
        }
        this.remainSec -= 1
      }, 1000)
      if (typeof document !== 'undefined') document.body.style.overflow = 'hidden'
    },
    onShut() {
      this.clearTimer()
      if (typeof document !== 'undefined') document.body.style.overflow = ''
    },
    clearTimer() {
      if (this.timerId != null) {
        clearInterval(this.timerId)
        this.timerId = null
      }
    },
    onClose() {
      this.$emit('close')
    }
  }
}
</script>

<style scoped>
.vip-pay-fade-enter-active,
.vip-pay-fade-leave-active {
  transition: opacity 0.22s ease;
}
.vip-pay-fade-enter,
.vip-pay-fade-leave-to {
  opacity: 0;
}
.vip-pay-panel {
  box-shadow: 0 24px 80px rgba(0, 0, 0, 0.55);
}
</style>
